// src/components/editor/EditorFormatBar.jsx
//
// Compact horizontal formatting bar shown above the document. It is additive:
// the full Format controls still live in the left Format sidebar panel — this
// bar calls the SAME handlers, so there is no duplicate formatting logic here.
//
// What earns a place is mid-sentence work. Bold, italic, styles, lists,
// highlight, page breaks — and font and size, which a manuscript needs per
// passage as much as per document once headings, epigraphs and scene breaks are
// in play. Line spacing and indent stay in the Format panel; those really are
// set once. That split matters more since the sidebar became a drill-down:
// opening the Format panel now hides the icon strip, so anything wanted
// mid-sentence really does need to be on this bar.
//
// All buttons use onMouseDown preventDefault so clicking them never blurs the
// contenteditable / collapses the user's selection before the command runs.
import React, { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, ListItemIcon, Tooltip, Divider } from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import TextIncreaseIcon from '@mui/icons-material/TextIncrease';
import TextDecreaseIcon from '@mui/icons-material/TextDecrease';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { HIGHLIGHT_COLORS } from './EditorToolbar';
// The same table the Format panel, the Manuscript Structure page and the cover
// designer read, so no toolbar can offer a font or size the others don't.
import { FONT_FAMILIES, FONT_SIZES } from '../../config/fonts';

// Heading levels and block quote share one menu rather than being a dropdown
// plus a stray button, because from the author's side they are one decision:
// what kind of block is this?
const STYLE_OPTIONS = [
  { label: 'Paragraph', level: 0 },
  { label: 'Heading 1', level: 1 },
  { label: 'Heading 2', level: 2 },
  { label: 'Heading 3', level: 3 },
  { label: 'Block quote', quote: true, Icon: FormatQuoteIcon },
];

const ALIGN_OPTIONS = [
  { label: 'Left', key: 'left', Icon: FormatAlignLeftIcon },
  { label: 'Center', key: 'center', Icon: FormatAlignCenterIcon },
  { label: 'Right', key: 'right', Icon: FormatAlignRightIcon },
  { label: 'Justify', key: 'justify', Icon: FormatAlignJustifyIcon },
];

const keepSelection = (e) => e.preventDefault();

const BarButton = ({ title, onClick, disabled, children }) => (
  <Tooltip title={title}>
    <span style={{ display: 'inline-flex' }}>
      <IconButton
        size="small"
        className="efb-btn"
        disabled={disabled}
        onMouseDown={keepSelection}
        onClick={onClick}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

const EditorFormatBar = ({
  onBold,
  onItalic,
  onUnderline,
  onStrikethrough,
  onQuote,
  onApplyHeading,
  onUndo,
  onRedo,
  onFontFamilyChange,
  onFontSizeChange,
  onBulletedList,
  onNumberedList,
  onAlignLeft,
  onAlignCenter,
  onAlignRight,
  onAlignJustify,
  onHighlightColorChange,
  onClearFormatting,
  onInsertPageBreak,
  onInsertSceneBreak,
  // The manuscript theme switcher (src/components/manuscriptTheme/). Not the
  // app's own theme: this changes the look of the author's book.
  themeName = '',
  onOpenThemes,
  canChangeTheme = true,
}) => {
  const [styleAnchor, setStyleAnchor] = useState(null);
  const [fontAnchor, setFontAnchor] = useState(null);
  const [sizeAnchor, setSizeAnchor] = useState(null);
  const [alignAnchor, setAlignAnchor] = useState(null);
  const [highlightAnchor, setHighlightAnchor] = useState(null);
  // What the two triggers read. These track the last choice made HERE rather
  // than the caret's actual formatting — the editor has no selection-formatting
  // feed to subscribe to, and the Format panel's own controls work the same way.
  const [fontName, setFontName] = useState('Source Serif 4');
  const [fontSize, setFontSize] = useState('16px');
  // Remembered so the swatch shows what one more click would apply, the way any
  // other highlighter works — marking a second passage in the same colour should
  // not mean reopening the menu.
  const [highlight, setHighlight] = useState(HIGHLIGHT_COLORS[0]);

  const pickStyle = (opt) => {
    setStyleAnchor(null);
    if (opt.quote) onQuote?.();
    else onApplyHeading?.(opt.level);
  };

  const pickFont = (name) => {
    setFontAnchor(null);
    setFontName(name);
    // The full CSS stack, not the bare name, so the platform fallbacks apply.
    onFontFamilyChange?.(FONT_FAMILIES.find((f) => f.name === name)?.stack ?? name);
  };

  const pickSize = (value) => {
    setSizeAnchor(null);
    setFontSize(value);
    onFontSizeChange?.(value);
  };

  // Steps through the shared size list rather than adding or subtracting px, so
  // the steppers can only ever land on a size the menu also offers.
  const stepSize = (direction) => {
    const i = FONT_SIZES.indexOf(fontSize);
    const next = FONT_SIZES[Math.min(FONT_SIZES.length - 1, Math.max(0, (i === -1 ? 6 : i) + direction))];
    if (next) pickSize(next);
  };

  const alignHandlers = {
    left: onAlignLeft,
    center: onAlignCenter,
    right: onAlignRight,
    justify: onAlignJustify,
  };
  const pickAlign = (key) => {
    setAlignAnchor(null);
    alignHandlers[key]?.();
  };

  const applyHighlight = (color) => {
    setHighlightAnchor(null);
    setHighlight(color);
    onHighlightColorChange?.(color === 'none' ? 'transparent' : color);
  };

  return (
    <Box className="editor-format-bar">
      <BarButton title="Undo" onClick={onUndo}><UndoIcon fontSize="small" /></BarButton>
      <BarButton title="Redo" onClick={onRedo}><RedoIcon fontSize="small" /></BarButton>

      <Divider orientation="vertical" flexItem className="efb-divider" />

      <Box
        component="button"
        type="button"
        className="efb-menu"
        onMouseDown={keepSelection}
        onClick={(e) => setStyleAnchor(e.currentTarget)}
      >
        Styles
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      </Box>
      <Menu anchorEl={styleAnchor} open={!!styleAnchor} onClose={() => setStyleAnchor(null)}>
        {STYLE_OPTIONS.map((opt) => (
          <MenuItem key={opt.label} onMouseDown={keepSelection} onClick={() => pickStyle(opt)}>
            {opt.Icon && <ListItemIcon><opt.Icon fontSize="small" /></ListItemIcon>}
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      <Box
        component="button"
        type="button"
        className="efb-menu efb-menu-font"
        title="Font"
        onMouseDown={keepSelection}
        onClick={(e) => setFontAnchor(e.currentTarget)}
      >
        <span className="efb-menu-label">{fontName}</span>
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      </Box>
      <Menu
        anchorEl={fontAnchor}
        open={!!fontAnchor}
        onClose={() => setFontAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 320 } } }}
      >
        {FONT_FAMILIES.map((f) => (
          <MenuItem
            key={f.name}
            onMouseDown={keepSelection}
            onClick={() => pickFont(f.name)}
            selected={f.name === fontName}
            /* Each name set in its own face, so the list is a specimen sheet
               rather than a list of words to imagine. */
            sx={{ fontFamily: f.stack }}
          >
            {f.name}
          </MenuItem>
        ))}
      </Menu>

      <Box
        component="button"
        type="button"
        className="efb-menu efb-menu-size"
        title="Font size"
        onMouseDown={keepSelection}
        onClick={(e) => setSizeAnchor(e.currentTarget)}
      >
        <span className="efb-menu-label">{parseInt(fontSize, 10)}</span>
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      </Box>
      <Menu
        anchorEl={sizeAnchor}
        open={!!sizeAnchor}
        onClose={() => setSizeAnchor(null)}
        slotProps={{ paper: { sx: { maxHeight: 320 } } }}
      >
        {FONT_SIZES.map((s) => (
          <MenuItem
            key={s}
            onMouseDown={keepSelection}
            onClick={() => pickSize(s)}
            selected={s === fontSize}
          >
            {parseInt(s, 10)}
          </MenuItem>
        ))}
      </Menu>

      <BarButton
        title="Increase font size"
        onClick={() => stepSize(1)}
        disabled={fontSize === FONT_SIZES[FONT_SIZES.length - 1]}
      >
        <TextIncreaseIcon fontSize="small" />
      </BarButton>
      <BarButton
        title="Decrease font size"
        onClick={() => stepSize(-1)}
        disabled={fontSize === FONT_SIZES[0]}
      >
        <TextDecreaseIcon fontSize="small" />
      </BarButton>

      <Divider orientation="vertical" flexItem className="efb-divider" />

      <BarButton title="Bold" onClick={onBold}><FormatBoldIcon fontSize="small" /></BarButton>
      <BarButton title="Italic" onClick={onItalic}><FormatItalicIcon fontSize="small" /></BarButton>
      <BarButton title="Underline" onClick={onUnderline}><FormatUnderlinedIcon fontSize="small" /></BarButton>
      <BarButton title="Strikethrough" onClick={onStrikethrough}><StrikethroughSIcon fontSize="small" /></BarButton>

      <Divider orientation="vertical" flexItem className="efb-divider" />

      <BarButton title="Bulleted list" onClick={onBulletedList}><FormatListBulletedIcon fontSize="small" /></BarButton>
      <BarButton title="Numbered list" onClick={onNumberedList}><FormatListNumberedIcon fontSize="small" /></BarButton>

      <Box
        component="button"
        type="button"
        className="efb-menu efb-menu-icon"
        title="Alignment"
        onMouseDown={keepSelection}
        onClick={(e) => setAlignAnchor(e.currentTarget)}
      >
        <FormatAlignLeftIcon sx={{ fontSize: 17 }} />
        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
      </Box>
      <Menu anchorEl={alignAnchor} open={!!alignAnchor} onClose={() => setAlignAnchor(null)}>
        {ALIGN_OPTIONS.map((opt) => (
          <MenuItem key={opt.key} onMouseDown={keepSelection} onClick={() => pickAlign(opt.key)}>
            <ListItemIcon><opt.Icon fontSize="small" /></ListItemIcon>
            {opt.label}
          </MenuItem>
        ))}
      </Menu>

      <Divider orientation="vertical" flexItem className="efb-divider" />

      <Tooltip title="Highlight">
        <Box
          component="button"
          type="button"
          className="efb-swatch"
          onMouseDown={keepSelection}
          onClick={(e) => setHighlightAnchor(e.currentTarget)}
        >
          <BorderColorIcon sx={{ fontSize: 16 }} />
          <span
            className="efb-swatch-bar"
            style={{ background: highlight === 'none' ? 'transparent' : highlight }}
          />
        </Box>
      </Tooltip>
      <Menu anchorEl={highlightAnchor} open={!!highlightAnchor} onClose={() => setHighlightAnchor(null)}>
        <Box className="efb-swatch-grid">
          {HIGHLIGHT_COLORS.map((c) => (
            <Box
              key={c}
              component="button"
              type="button"
              title={c === 'none' ? 'No highlight' : c}
              className={`efb-swatch-chip${c === 'none' ? ' is-none' : ''}${highlight === c ? ' is-current' : ''}`}
              style={{ background: c === 'none' ? 'transparent' : c }}
              onMouseDown={keepSelection}
              onClick={() => applyHighlight(c)}
            />
          ))}
        </Box>
      </Menu>

      <BarButton title="Clear formatting" onClick={onClearFormatting}>
        <FormatClearIcon fontSize="small" />
      </BarButton>

      <Divider orientation="vertical" flexItem className="efb-divider" />

      <BarButton title="Insert page break" onClick={onInsertPageBreak}>
        <InsertPageBreakIcon fontSize="small" />
      </BarButton>
      {/* A scene break is one block the theme draws (asterism, fleuron…), so
          changing the theme restyles every break in the book at once. */}
      <BarButton title="Insert scene break" onClick={onInsertSceneBreak}>
        <MoreHorizIcon fontSize="small" />
      </BarButton>

      <Divider orientation="vertical" flexItem className="efb-divider" />

      <Tooltip title={canChangeTheme ? 'Theme — the look of the whole manuscript' : 'Only the author can change the theme'}>
        <span style={{ display: 'inline-flex' }}>
          <Box
            component="button"
            type="button"
            className="efb-menu efb-menu-theme"
            disabled={!canChangeTheme}
            onMouseDown={keepSelection}
            onClick={(e) => onOpenThemes?.(e.currentTarget)}
          >
            <PaletteOutlinedIcon sx={{ fontSize: 17 }} />
            <span className="efb-menu-label">{themeName || 'No theme'}</span>
            <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
          </Box>
        </span>
      </Tooltip>

      {/* Placeholder — branch/versioning behavior is a separate story */}
      <BarButton title="Branch (coming soon)" disabled>
        <AccountTreeIcon fontSize="small" />
      </BarButton>
    </Box>
  );
};

export default EditorFormatBar;
